import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import StatCard from '../../../components/common/StatCard';
import AlertToast from '../../../components/common/AlertToast';
import ViewToggle, { readViewPreference } from '../../../components/common/ViewToggle';
import CoursesAnalytics from '../../../components/analytics/CoursesAnalyticsV2';

const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

const BSModal = ({ show, onClose, title, size = '', children, footer }) =>
  show ? (
    <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1050 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal-dialog modal-dialog-scrollable ${size} modal-dialog-centered`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  ) : null;

const blankCourse = () => ({
  courseId: '', courseCode: '', courseName: '', category: '', url: '', websiteSlug: '', notes: '',
  totalFees: '', minPrice: '', maxPrice: '', discountPercent: '',
  numberOfMonths: '', totalHours: '',
  prerequisites: '', skills: '', status: 'active', quizzes: [],
  visibilityStatus: 'published',
  courseColor: '#3B82F6',
  shortDescription: '',
  thumbnailUrl: '',
  promoImageUrl: '',
  promoVideoUrl: '',
  priceChangeReason: '',
});
const isSelfServeCategory = (category) => /^(?:on[\s_-]?demand|free(?:[\s_-]?course)?|open[\s_-]?access)$/i.test(String(category || '').trim());
const COURSE_THUMBNAIL_MAX_BYTES = 5 * 1024 * 1024;

// Curated brand palette for the "Choose Color" picker. Same colour
// across timetable / batch calendar / staff view (Issue 2 of the
// requirement). Admin can also pick any custom hex via the input
// below the swatches.
const COURSE_COLOR_PALETTE = [
  '#3B82F6', // blue
  '#10B981', // emerald
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
  '#F97316', // orange
  '#6366F1', // indigo
  '#14B8A6', // teal
  '#A855F7', // purple
];

// Quick inline color picker for the courses table. Click the
// palette icon → dropdown of swatches → pick one → PATCH /color
// → row updates in place. Avoids forcing the admin to open the
// full edit dialog just to recolour a course.
const ColorPickerCell = ({ course, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  // Close on outside click. Tiny effect, no perf impact — this
  // dropdown only opens on explicit user action.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const pick = async (color) => {
    if (color === course.courseColor) { setOpen(false); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/training/courses/${course._id}/color`, { courseColor: color });
      onSaved?.(r.data?.data || { ...course, courseColor: color });
      setOpen(false);
    } catch (e) {
      alert(e.response?.data?.message || 'Could not save color.');
    }
    setSaving(false);
  };

  return (
    <div className="position-relative" ref={ref}>
      <button type="button"
        className="btn btn-outline-secondary btn-sm"
        title="Quick change color"
        disabled={saving}
        onClick={() => setOpen(o => !o)}>
        <i className="bi bi-palette" />
      </button>
      {open && (
        <div className="position-absolute end-0 mt-1 p-2 shadow"
          style={{ zIndex: 50, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, minWidth: 180 }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6, fontWeight: 600 }}>
            Pick a colour
          </div>
          <div className="d-flex flex-wrap gap-1 mb-2" style={{ maxWidth: 168 }}>
            {COURSE_COLOR_PALETTE.map(c => (
              <button key={c} type="button"
                title={c}
                onClick={() => pick(c)}
                style={{
                  width: 22, height: 22, borderRadius: 4, padding: 0, cursor: 'pointer',
                  background: c, border: c === course.courseColor ? '2px solid #0f172a' : '1px solid #e2e8f0',
                }}
              />
            ))}
          </div>
          <div className="d-flex align-items-center gap-1">
            <input type="color" className="form-control form-control-color form-control-sm"
              style={{ width: 30, height: 26, padding: 0 }}
              defaultValue={/^#[0-9a-fA-F]{6}$/.test(course.courseColor || '') ? course.courseColor : '#3B82F6'}
              onChange={e => pick(e.target.value)}
              title="Custom hex" />
            <input type="text" className="form-control form-control-sm" style={{ fontSize: 11, padding: '2px 6px' }}
              defaultValue={course.courseColor || ''} placeholder="#hex"
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); pick(e.currentTarget.value); } }} />
          </div>
        </div>
      )}
    </div>
  );
};

const blankQuiz = () => ({
  title: '', description: '', totalMarks: 10, passingMarks: 5, timeLimit: 0,
  shuffleQuestions: false, allowNegativeMarking: false, negativeMarkingValue: 0.25, questions: [],
});

const blankQuestion = () => ({
  type: 'mcq', text: '', options: ['', '', '', ''], correctAnswer: 0,
  marks: 1, negativeMarks: 0, explanation: '', fillAnswer: '', codingPrompt: '', testCases: [],
});

export default function Courses() {
  const { user, isSuperAdmin } = useAuth();
  const roleKey = user?.roleKey || '';
  const canWrite = isSuperAdmin || ['admin', 'manager', 'team_lead'].includes(roleKey);
  const canAdmin = isSuperAdmin || ['admin'].includes(roleKey);
  const canSeeAnalytics = canWrite;
  const location = useLocation();
  const navigate = useNavigate();

  // View toggle (table ↔ analytics), persisted per-user.
  const initialPageTab = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    return qs.get('tab') === 'requests' ? 'requests' : 'courses';
  }, [location.search]);
  const [view, setView] = useState(() => readViewPreference('courses', 'table'));
  const [pageTab, setPageTab] = useState(initialPageTab);

  const [courses, setCourses] = useState([]);
  const [stats, setStats]     = useState({ total: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [categories, setCategories]     = useState([]); // distinct categories for dropdown
  const [courseCodes, setCourseCodes]     = useState([]); // distinct course codes for dropdown
  const [page, setPage]       = useState(1);
  const [total, setTotal]     = useState(0);
  // Per-column header filters. Each filter applies an AND across
  // columns and an AND against the top-bar status / category dropdowns.
  // Category, CourseCode, Status are dropdowns sourced from the
  // loaded courses so the options are always in sync.
  const [colFilters, setColFilters] = useState({
    courseName: '', category: '', courseCode: '', status: '',
  });
  const setCol = (k, v) => setColFilters(prev => ({ ...prev, [k]: v }));
  const LIMIT = 20;

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(blankCourse());
  const [courseAssets, setCourseAssets] = useState({ thumbnail: null, promoImage: null });
  const [activeTab, setActiveTab] = useState('details');
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');

  const [editingQuizIdx, setEditingQuizIdx] = useState(null);
  const [quizForm, setQuizForm] = useState(blankQuiz());
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [editingQIdx, setEditingQIdx] = useState(null);
  const [qForm, setQForm]       = useState(blankQuestion());
  const [showQModal, setShowQModal] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast]       = useState({ show: false, message: '', type: 'success' });
  const [priceRequests, setPriceRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestFilter, setRequestFilter] = useState({ status: '', search: '' });
  const [requestBusyId, setRequestBusyId] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const pricingChangedInForm = useMemo(() => {
    if (!editing) return false;
    return (
      Number(form.totalFees || 0) !== Number(editing.totalFees || 0) ||
      Number(form.minPrice || 0) !== Number(editing.minPrice || 0) ||
      Number(form.maxPrice || 0) !== Number(editing.maxPrice || 0) ||
      Number(form.discountPercent || 0) !== Number(editing.discountPercent || 0)
    );
  }, [editing, form.totalFees, form.minPrice, form.maxPrice, form.discountPercent]);

  const notify = (message, type = 'success') => setToast({ show: true, message, type });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (search)         params.search = search;
      if (filterStatus)   params.status = filterStatus;
      if (filterCategory) params.category = filterCategory;
      const [r, s] = await Promise.all([
        api.get('/training/courses', { params }),
        api.get('/training/courses/stats/overview').catch(() => null),
      ]);
      setCourses(r.data.data || []);
      setTotal(r.data.pagination?.total || 0);
      if (s) setStats(s.data.data ?? s.data ?? {});
    } catch (_) {}
    setLoading(false);
  }, [page, search, filterStatus, filterCategory]);

  // Load distinct categories + course codes for the per-column filter
  // dropdowns. We hit the dedicated endpoint for categories and derive
  // course codes client-side from already-loaded courses. Both auto-
  // refresh when courses change so new values appear without a reload.
  useEffect(() => {
    api.get('/training/courses/categories').then(r => {
      setCategories(r.data?.data || []);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    const codes = Array.from(new Set(
      courses.map(c => c.courseCode).filter(Boolean)
    )).sort();
    setCourseCodes(codes);
  }, [courses]);

  // Per-column client-side filter. Each input in the second <tr> applies
  // an AND across columns. Empty values = no filter on that column.
  // Numeric inputs (fees/duration/hours/quizzes) filter with a minimum value.
  const filteredCourses = useMemo(() => {
    const cf = colFilters;
    const norm = (v) => String(v || '').toLowerCase().trim();
    return courses.filter(c => {
      if (cf.courseName && !norm(c.courseName).includes(norm(cf.courseName))) return false;
      if (cf.category   && !norm(c.category).includes(norm(cf.category)))     return false;
      if (cf.courseCode && !norm(c.courseCode).includes(norm(cf.courseCode))) return false;
      if (cf.status     && c.status !== cf.status)                              return false;
      // Numeric minimums
      const minFees     = parseFloat(cf.fees);
      const minDuration = parseFloat(cf.duration);
      const minHours    = parseFloat(cf.hours);
      const minQuizzes  = parseFloat(cf.quizzes);
      if (!isNaN(minFees)     && (c.totalFees       || 0) < minFees)     return false;
      if (!isNaN(minDuration) && (c.numberOfMonths || 0) < minDuration) return false;
      if (!isNaN(minHours)    && (c.totalHours      || 0) < minHours)    return false;
      if (!isNaN(minQuizzes)  && ((c.quizzes || []).length) < minQuizzes) return false;
      return true;
    });
  }, [courses, colFilters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setPageTab(initialPageTab);
  }, [initialPageTab]);

  const loadPriceRequests = useCallback(async () => {
    if (!canAdmin) return;
    setRequestsLoading(true);
    try {
      const r = await api.get('/training/courses/price-change-requests', {
        params: {
          status: requestFilter.status || undefined,
          search: requestFilter.search || undefined,
        },
      });
      const rows = r.data?.records || r.data?.data || [];
      console.log('[course-price-tab] response', {
        status: r.status,
        count: Array.isArray(rows) ? rows.length : 0,
        rows,
      });
      setPriceRequests(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error('[course-price-tab] load failed', {
        message: e.response?.data?.message || e.message,
        status: e.response?.status,
        data: e.response?.data,
      });
      setPriceRequests([]);
      notify(e.response?.data?.message || 'Could not load course correction requests.', 'danger');
    }
    setRequestsLoading(false);
  }, [canAdmin, requestFilter.search, requestFilter.status]);

  useEffect(() => {
    if (pageTab === 'requests') loadPriceRequests();
  }, [pageTab, loadPriceRequests]);

  const handleApproveRequest = async (requestId) => {
    setRequestBusyId(requestId);
    try {
      await api.post(`/training/courses/price-requests/${requestId}/approve`, {});
      notify('Course price change approved.');
      await Promise.all([loadPriceRequests(), load()]);
    } catch (e) {
      notify(e.response?.data?.message || 'Approval failed.', 'danger');
    }
    setRequestBusyId('');
  };

  const handleRejectRequest = async () => {
    if (!rejectTarget?._id) return;
    setRequestBusyId(rejectTarget._id);
    try {
      await api.post(`/training/courses/price-requests/${rejectTarget._id}/reject`, {
        rejectionReason: rejectReason,
      });
      notify('Course price change rejected.', 'warning');
      setRejectTarget(null);
      setRejectReason('');
      await loadPriceRequests();
    } catch (e) {
      notify(e.response?.data?.message || 'Rejection failed.', 'danger');
    }
    setRequestBusyId('');
  };

  const handleDeleteRequest = async (row) => {
    if (!row?._id) return;
    const ok = window.confirm(`Delete the course correction request for ${row.course?.courseName || 'this course'}?`);
    if (!ok) return;
    setRequestBusyId(row._id);
    try {
      await api.delete(`/training/courses/price-requests/${row._id}`);
      notify('Course correction request deleted.', 'warning');
      await loadPriceRequests();
    } catch (e) {
      notify(e.response?.data?.message || 'Delete failed.', 'danger');
    }
    setRequestBusyId('');
  };

  const discountedPreview = form.totalFees && form.discountPercent
    ? (parseFloat(form.totalFees) * (1 - parseFloat(form.discountPercent) / 100)).toFixed(2)
    : null;

  const openNew = () => {
    setEditing(null);
    setForm(blankCourse());
    setCourseAssets({ thumbnail: null, promoImage: null });
    setActiveTab('details');
    setFormError('');
    setShowForm(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      courseId: c.courseId || '', courseCode: c.courseCode || '', courseName: c.courseName || '',
      category: c.category || '',
      url: c.url || '',
      websiteSlug: c.websiteSlug || '',
      notes: c.notes || c.description || '',
      shortDescription: c.shortDescription || '',
      thumbnailUrl: c.thumbnailUrl || '',
      promoImageUrl: c.promoImageUrl || '',
      promoVideoUrl: c.promoVideoUrl || '',
      totalFees: c.totalFees ?? '', minPrice: c.minPrice ?? '', maxPrice: c.maxPrice ?? '',
      discountPercent: c.discountPercent ?? '', numberOfMonths: c.numberOfMonths ?? '', totalHours: c.totalHours ?? '',
      prerequisites: (c.prerequisites || []).join(', '),
      skills: (c.skills || []).join(', '),
      status: c.status || 'active', quizzes: c.quizzes || [],
      visibilityStatus: c.visibilityStatus || 'published',
      courseColor: c.courseColor || '#3B82F6',
      priceChangeReason: '',
    });
    setCourseAssets({ thumbnail: null, promoImage: null });
    setActiveTab('details'); setFormError(''); setShowForm(true);
  };

  const handleThumbnailChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setCourseAssets(prev => ({ ...prev, thumbnail: null }));
      return;
    }
    if (!String(file.type || '').startsWith('image/')) {
      setFormError('Course thumbnail must be an image file');
      event.target.value = '';
      return;
    }
    if (file.size > COURSE_THUMBNAIL_MAX_BYTES) {
      setFormError('Course thumbnail must be 5 MB or smaller');
      event.target.value = '';
      return;
    }
    setFormError('');
    setCourseAssets(prev => ({ ...prev, thumbnail: file }));
  };

  const handleSave = async () => {
    setFormError('');
    if (!form.courseName.trim()) { setFormError('Course name is required'); return; }
    if (!form.courseId.trim())   { setFormError('Course ID is required'); return; }
    if (!form.courseCode.trim()) { setFormError('Course code is required'); return; }
    if (!form.category.trim())   { setFormError('Category is required'); return; }
    if ((!editing || editing.websiteSlug) && !String(form.websiteSlug || '').trim()) {
      setFormError('Website course slug is required');
      return;
    }
    if (pricingChangedInForm && !String(form.priceChangeReason || '').trim()) {
      setFormError('Reason is required when changing course price');
      return;
    }
    setSaving(true);
    try {
      const lmsMode = isSelfServeCategory(form.category);
      const payload = new FormData();
      payload.append('courseId', String(form.courseId || '').trim());
      payload.append('courseCode', String(form.courseCode || '').trim());
      payload.append('courseName', String(form.courseName || '').trim());
      payload.append('category', String(form.category || '').trim());
      payload.append('url', String(form.url || '').trim());
      payload.append('websiteSlug', String(form.websiteSlug || '').trim());
      payload.append('notes', String(form.notes || '').trim());
      payload.append('shortDescription', String(form.shortDescription || '').trim());
      payload.append('thumbnailUrl', String(form.thumbnailUrl || '').trim());
      payload.append('promoImageUrl', String(form.promoImageUrl || '').trim());
      payload.append('promoVideoUrl', String(form.promoVideoUrl || '').trim());
      payload.append('status', String(form.status || 'active'));
      payload.append('visibilityStatus', String(form.visibilityStatus || 'published'));
      payload.append('courseColor', /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(String(form.courseColor || '').trim())
        ? String(form.courseColor).trim()
        : '#3B82F6');
      payload.append('prerequisites', JSON.stringify(form.prerequisites ? form.prerequisites.split(',').map(s => s.trim()).filter(Boolean) : []));
      payload.append('skills', JSON.stringify(form.skills ? form.skills.split(',').map(s => s.trim()).filter(Boolean) : []));
      payload.append('priceChangeReason', String(form.priceChangeReason || '').trim());
      payload.append('totalFees', String(lmsMode ? 0 : (parseFloat(form.totalFees) || 0)));
      payload.append('minPrice', String(lmsMode ? 0 : (parseFloat(form.minPrice) || 0)));
      payload.append('maxPrice', String(lmsMode ? 0 : (parseFloat(form.maxPrice) || 0)));
      payload.append('discountPercent', String(lmsMode ? 0 : (parseFloat(form.discountPercent) || 0)));
      payload.append('numberOfMonths', String(lmsMode ? 1 : (parseFloat(form.numberOfMonths) || 0)));
      payload.append('totalHours', String(lmsMode ? 1 : (parseFloat(form.totalHours) || 0)));
      if (courseAssets.thumbnail) payload.append('thumbnail', courseAssets.thumbnail);
      if (courseAssets.promoImage) payload.append('promoImage', courseAssets.promoImage);
      if (editing) await api.put(`/training/courses/${editing._id}`, payload);
      else          await api.post('/training/courses', payload);
      notify(`Course ${editing ? 'updated' : 'created'}!`);
      setShowForm(false); load();
    } catch (err) { setFormError(err.response?.data?.message || 'Error saving'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/training/courses/${deleteTarget._id}`);
      notify('Course deleted', 'warning');
      setDeleteTarget(null); load();
    } catch (err) { notify(err.response?.data?.message || 'Delete failed', 'danger'); }
    setDeleting(false);
  };

  // Quiz helpers
  const openAddQuiz  = () => { setEditingQuizIdx(null); setQuizForm(blankQuiz()); setShowQuizModal(true); };
  const openEditQuiz = (idx) => { setEditingQuizIdx(idx); setQuizForm({ ...form.quizzes[idx] }); setShowQuizModal(true); };
  const saveQuiz = () => {
    const updated = [...form.quizzes];
    if (editingQuizIdx !== null) updated[editingQuizIdx] = quizForm;
    else updated.push(quizForm);
    setForm(f => ({ ...f, quizzes: updated }));
    setShowQuizModal(false);
  };
  const removeQuiz = (idx) => setForm(f => ({ ...f, quizzes: f.quizzes.filter((_, i) => i !== idx) }));

  // Question helpers
  const openAddQ  = () => { setEditingQIdx(null); setQForm(blankQuestion()); setShowQModal(true); };
  const openEditQ = (idx) => { setEditingQIdx(idx); setQForm({ ...quizForm.questions[idx] }); setShowQModal(true); };
  const saveQ = () => {
    const updated = [...(quizForm.questions || [])];
    if (editingQIdx !== null) updated[editingQIdx] = qForm;
    else updated.push(qForm);
    setQuizForm(q => ({ ...q, questions: updated }));
    setShowQModal(false);
  };
  const removeQ = (idx) => setQuizForm(q => ({ ...q, questions: q.questions.filter((_, i) => i !== idx) }));

  const pages = Math.ceil(total / LIMIT);
  const lmsMode = isSelfServeCategory(form.category);

  return (
    <div>
      <PageHeader title="Courses" icon="bi-book" subtitle={`${total} total`}
        actions={
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <div className="input-group" style={{ width: 220 }}>
              <span className="input-group-text"><i className="bi bi-search"></i></span>
              <input className="form-control" placeholder="Search…" value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-select" style={{ width: 140 }} value={filterStatus}
              onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            {/* Category filter — populated from /training/courses/categories */}
            <select className="form-select" style={{ width: 180 }} value={filterCategory}
              onChange={e => { setFilterCategory(e.target.value); setPage(1); }}
              title="Filter by category">
              <option value="">All Categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {canWrite && (
              <>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  title="Backfill courseColor for any course that doesn't have one yet. Idempotent."
                  onClick={async () => {
                    try {
                      const r = await api.post('/training/batch-calendar/migrate-colors');
                      const d = r.data?.data || {};
                      notify(`Backfilled ${d.updated || 0} course colors (${d.total || 0} missing).`);
                      load();
                    } catch (e) {
                      notify(e.response?.data?.message || 'Backfill failed.', 'danger');
                    }
                  }}
                >
                  <i className="bi bi-palette me-1"></i>Backfill Colors
                </button>
                <button className="btn btn-primary btn-sm" onClick={openNew}>
                  <i className="bi bi-plus-lg me-1"></i>Add Course
                </button>
              </>
            )}
          </div>
        }
      />

      {/* View toggle (Table ↔ Analytics) — analytics is management-only */}
      {canAdmin && (
        <div className="mb-3">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button className={`nav-link ${pageTab === 'courses' ? 'active' : ''}`} onClick={() => setPageTab('courses')}>
                Courses
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${pageTab === 'requests' ? 'active' : ''}`} onClick={() => setPageTab('requests')}>
                Correction Requests
              </button>
            </li>
          </ul>
        </div>
      )}

      {pageTab === 'courses' && canSeeAnalytics && (
        <div className="d-flex justify-content-end mb-3">
          <ViewToggle
            storageKey="courses"
            value={view}
            onChange={setView}
          />
        </div>
      )}

      {/* Analytics view (renders ABOVE the table) */}
      {pageTab === 'courses' && canSeeAnalytics && view === 'analytics' && (
        <div className="mb-4">
          <CoursesAnalytics />
        </div>
      )}

      {pageTab === 'courses' && (!canSeeAnalytics || view === 'table') && (
      <>
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3"><StatCard title="Total" value={stats.totalCourses ?? 0} icon="bi-book" color="#6366f1" /></div>
        <div className="col-6 col-md-3"><StatCard title="Active" value={stats.activeCourses ?? 0} icon="bi-check-circle" color="#22c55e" /></div>
        <div className="col-6 col-md-3"><StatCard title="Inactive" value={(stats.totalCourses||0)-(stats.activeCourses||0)} icon="bi-pause-circle" color="#f59e0b" /></div>
        <div className="col-6 col-md-3"><StatCard title="On Page" value={courses.length} icon="bi-grid" color="#06b6d4" /></div>
      </div>

      {/* Inline form */}
      {showForm && (
        <div className="card mb-4 border-primary">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span className="fw-semibold"><i className="bi bi-book me-2"></i>{editing ? 'Edit Course' : 'New Course'}</span>
            <button className="btn-close" onClick={() => setShowForm(false)}></button>
          </div>
          <div className="card-body">
            {formError && <div className="alert alert-danger py-2 mb-3">{formError}</div>}
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>Course Details</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'quizzes' ? 'active' : ''}`} onClick={() => setActiveTab('quizzes')}>
                  Quizzes {form.quizzes.length > 0 && <span className="badge bg-primary ms-1">{form.quizzes.length}</span>}
                </button>
              </li>
            </ul>

            {activeTab === 'details' && (
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Course ID *</label>
                  <input className="form-control" value={form.courseId} placeholder="e.g. MERN001"
                    onChange={e => setForm(f => ({ ...f, courseId: e.target.value }))} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Course Code *</label>
                  <input className="form-control" value={form.courseCode} placeholder="e.g. MERN"
                    onChange={e => setForm(f => ({ ...f, courseCode: e.target.value }))} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Course Name *</label>
                  <input className="form-control" value={form.courseName}
                    onChange={e => setForm(f => ({ ...f, courseName: e.target.value }))} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Category *</label>
                  <input
                    className={`form-control ${formError.includes('Category') ? 'is-invalid' : ''}`}
                    list="course-category-options"
                    value={form.category}
                    placeholder="Type or pick — e.g. Programming, Data Science"
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
                  <datalist id="course-category-options">
                    {/* Previously-used categories (A→Z) from this tenant */}
                    {categories.map(c => <option key={c} value={c} />)}
                  </datalist>
                  {formError.includes('Category') && (
                    <div className="invalid-feedback d-block">Category is required.</div>
                  )}
                </div>
                <div className="col-md-1">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label mb-2">Student Visibility</label>
                  <div className="d-flex flex-wrap gap-2" role="group" aria-label="Course student visibility">
                    {[
                      { value: 'published', label: 'Publish', icon: 'bi-check-circle', activeClass: 'btn-success' },
                      { value: 'draft', label: 'Draft', icon: 'bi-file-earmark', activeClass: 'btn-warning' },
                      { value: 'hidden_from_college', label: "Don't show to College Portal", icon: 'bi-building-slash', activeClass: 'btn-secondary' },
                    ].map(option => {
                      const selected = form.visibilityStatus === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={`btn btn-sm ${selected ? option.activeClass : 'btn-outline-secondary'}`}
                          aria-pressed={selected}
                          onClick={() => setForm(current => ({ ...current, visibilityStatus: option.value }))}
                        >
                          <i className={`bi ${option.icon} me-1`} />{option.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="form-text">
                    Draft courses are hidden from all students. College-hidden courses remain available to regular paid-course students.
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label">
                    Page URL <span className="text-muted small">(optional — link to a landing page)</span>
                  </label>
                  <input
                    type="url"
                    className="form-control"
                    value={form.url}
                    placeholder="https://example.com/courses/python-101"
                    onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
                </div>
                <div className="col-12">
                  <label className="form-label">Website Course Slug {(!editing || editing.websiteSlug) && <span className="text-danger">*</span>}</label>
                  <input className="form-control" required={!editing || Boolean(editing.websiteSlug)} value={form.websiteSlug} placeholder="course-python"
                    onChange={e => setForm(f => ({ ...f, websiteSlug: e.target.value }))} />
                </div>
                <div className="col-12">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" rows={2} value={form.notes}
                    placeholder="Internal notes for admin or staff use"
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
                <div className="col-12">
                  <label className="form-label">Short Description</label>
                  <textarea className="form-control" rows={2} value={form.shortDescription}
                    placeholder="Short catalog summary for on-demand and membership pages"
                    onChange={e => setForm(f => ({ ...f, shortDescription: e.target.value }))} />
                </div>
                <div className="col-12">
                  <label className="form-label">
                    Course Thumbnail <span className="text-muted small">(optional)</span>
                  </label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                    className="form-control"
                    onChange={handleThumbnailChange}
                  />
                  <div className="form-text">
                    Recommended: 1280x720 (16:9), JPG, PNG or WebP, up to 5 MB. A course-based visual is shown when no thumbnail is uploaded.
                  </div>
                  {(courseAssets.thumbnail || form.thumbnailUrl) && (
                    <div className="small text-success mt-1">
                      <i className="bi bi-image me-1" />
                      {courseAssets.thumbnail?.name || 'Current course thumbnail'}
                    </div>
                  )}
                </div>
                {lmsMode ? (
                  <>
                    <div className="col-md-6">
                      <label className="form-label">Promo Image</label>
                      <input type="file" accept="image/*" className="form-control"
                        onChange={e => setCourseAssets(prev => ({ ...prev, promoImage: e.target.files?.[0] || null }))} />
                      {(courseAssets.promoImage || form.promoImageUrl) && (
                        <div className="small text-muted mt-1">{courseAssets.promoImage?.name || form.promoImageUrl}</div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Promotion Video URL <span className="text-muted small">(optional)</span></label>
                      <input className="form-control" value={form.promoVideoUrl}
                        placeholder="https://youtube.com/... or hosted promo video"
                        onChange={e => setForm(f => ({ ...f, promoVideoUrl: e.target.value }))} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-md-3">
                      <label className="form-label">Total Fees (₹)</label>
                      <input type="number" className="form-control" value={form.totalFees}
                        onChange={e => setForm(f => ({ ...f, totalFees: e.target.value }))} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Discount %</label>
                      <input type="number" className="form-control" value={form.discountPercent} min={0} max={100}
                        onChange={e => setForm(f => ({ ...f, discountPercent: e.target.value }))} />
                      {discountedPreview && <small className="text-success">→ ₹{discountedPreview}</small>}
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Min Price (₹)</label>
                      <input type="number" className="form-control" value={form.minPrice}
                        onChange={e => setForm(f => ({ ...f, minPrice: e.target.value }))} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Max Price (₹)</label>
                      <input type="number" className="form-control" value={form.maxPrice}
                        onChange={e => setForm(f => ({ ...f, maxPrice: e.target.value }))} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Duration (Months)</label>
                      <input type="number" className="form-control" value={form.numberOfMonths}
                        onChange={e => setForm(f => ({ ...f, numberOfMonths: e.target.value }))} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Total Hours</label>
                      <input type="number" className="form-control" value={form.totalHours}
                        onChange={e => setForm(f => ({ ...f, totalHours: e.target.value }))} />
                    </div>
                  </>
                )}
                {editing && pricingChangedInForm && (
                  <div className="col-12">
                    <label className="form-label">Reason for Price Change *</label>
                    <textarea
                      className={`form-control ${formError.includes('Reason') ? 'is-invalid' : ''}`}
                      rows={2}
                      value={form.priceChangeReason || ''}
                      placeholder="Why is this price change required?"
                      onChange={e => setForm(f => ({ ...f, priceChangeReason: e.target.value }))}
                    />
                    <div className="form-text">
                      This reason will be shown in the admin approval mail and the correction request tab.
                    </div>
                  </div>
                )}
                <div className="col-md-6">
                  <label className="form-label">Prerequisites <small className="text-muted">(comma separated)</small></label>
                  <input className="form-control" value={form.prerequisites} placeholder="HTML, CSS, JavaScript"
                    onChange={e => setForm(f => ({ ...f, prerequisites: e.target.value }))} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Skills Covered <small className="text-muted">(comma separated)</small></label>
                  <input className="form-control" value={form.skills} placeholder="React, Node, MongoDB"
                    onChange={e => setForm(f => ({ ...f, skills: e.target.value }))} />
                </div>
              </div>
            )}

            {/* Course Color picker — same colour is used on the
                timetable cards, batch calendar, and staff view so
                the user can find "Python" at a glance. */}
            {activeTab === 'details' && (
              <div className="mt-3 pt-3 border-top">
                <label className="form-label fw-semibold">
                  <i className="bi bi-palette me-1" />Course Color
                  <span className="text-muted fw-normal small ms-2">
                    — used in timetable, batch calendar & staff view
                  </span>
                </label>
                <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                  {COURSE_COLOR_PALETTE.map(c => (
                    <button key={c} type="button"
                      title={c}
                      onClick={() => setForm(f => ({ ...f, courseColor: c }))}
                      style={{
                        width: 28, height: 28, borderRadius: 6, padding: 0, cursor: 'pointer',
                        background: c, border: form.courseColor === c ? '3px solid #0f172a' : '2px solid #e2e8f0',
                        boxShadow: form.courseColor === c ? '0 0 0 2px #fff inset' : 'none',
                        transition: 'transform .12s ease',
                      }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                      onMouseLeave={e => e.currentTarget.style.transform = ''}
                    />
                  ))}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <input type="color" className="form-control form-control-color"
                    style={{ width: 50, height: 32, padding: 2 }}
                    value={/^#[0-9a-fA-F]{6}$/.test(form.courseColor || '') ? form.courseColor : '#3B82F6'}
                    onChange={e => setForm(f => ({ ...f, courseColor: e.target.value }))}
                    title="Custom hex" />
                  <input type="text" className="form-control" style={{ maxWidth: 140 }}
                    value={form.courseColor || ''} placeholder="#3B82F6"
                    onChange={e => setForm(f => ({ ...f, courseColor: e.target.value }))} />
                  <span className="badge" style={{ background: form.courseColor, color: '#fff' }}>
                    Preview
                  </span>
                  <span className="text-muted small ms-auto">
                    <i className="bi bi-info-circle me-1" />
                    Choose a colour that contrasts well with white text.
                  </span>
                </div>
              </div>
            )}

            {activeTab === 'quizzes' && (
              <div>
                <button className="btn btn-outline-primary btn-sm mb-3" onClick={openAddQuiz}>
                  <i className="bi bi-plus me-1"></i>Attach Quiz
                </button>
                {form.quizzes.length === 0 && <p className="text-muted small">No quizzes attached yet.</p>}
                {form.quizzes.map((q, idx) => (
                  <div key={idx} className="card mb-2 bg-light border-0">
                    <div className="card-body py-2 d-flex justify-content-between align-items-center">
                      <div>
                        <strong className="me-2">{q.title || `Quiz ${idx + 1}`}</strong>
                        <span className="badge bg-secondary me-1">{q.questions?.length || 0}Q</span>
                        <span className="badge bg-light text-dark me-1">{q.totalMarks}M</span>
                        {q.timeLimit > 0 && <span className="badge bg-light text-dark">{q.timeLimit}min</span>}
                      </div>
                      <div className="d-flex gap-1">
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => openEditQuiz(idx)}><i className="bi bi-pencil"></i></button>
                        <button className="btn btn-outline-danger btn-sm" onClick={() => removeQuiz(idx)}><i className="bi bi-trash"></i></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="d-flex gap-2 mt-3 pt-2 border-top">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                {saving && <span className="spinner-border spinner-border-sm me-1"></span>}
                {editing ? 'Save Changes' : 'Create Course'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Course</th>
                  <th>Category</th>
                  <th>Code</th>
                  <th>Fees</th>
                  <th>Duration</th>
                  <th>Hours</th>
                  <th>Quizzes</th>
                  <th>Status</th>
                  <th>URL</th>
                  <th>Syllabus</th>
                  {canWrite && <th className="text-end">Actions</th>}
                </tr>
                {/* Per-column filters — second header row. Category, Course
                    Code and Status are dropdowns sourced from the
                    backend/loaded courses. Each control applies an AND
                    against the other columns. */}
                <tr>
                  <th>
                    <input className="form-control form-control-sm" placeholder="Filter course…"
                      value={colFilters.courseName} onChange={e => setCol('courseName', e.target.value)} />
                  </th>
                  <th>
                    <select className="form-select form-select-sm" value={colFilters.category}
                      onChange={e => setCol('category', e.target.value)}>
                      <option value="">All categories</option>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </th>
                  <th>
                    <select className="form-select form-select-sm" value={colFilters.courseCode}
                      onChange={e => setCol('courseCode', e.target.value)}>
                      <option value="">All codes</option>
                      {courseCodes.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </th>
                  <th>
                    <input type="number" className="form-control form-control-sm" placeholder="Min fees"
                      value={colFilters.fees} onChange={e => setCol('fees', e.target.value)} />
                  </th>
                  <th>
                    <input type="number" className="form-control form-control-sm" placeholder="Min mo"
                      value={colFilters.duration} onChange={e => setCol('duration', e.target.value)} />
                  </th>
                  <th>
                    <input type="number" className="form-control form-control-sm" placeholder="Min hrs"
                      value={colFilters.hours} onChange={e => setCol('hours', e.target.value)} />
                  </th>
                  <th>
                    <input type="number" className="form-control form-control-sm" placeholder="Min quiz"
                      value={colFilters.quizzes} onChange={e => setCol('quizzes', e.target.value)} />
                  </th>
                  <th>
                    <select className="form-select form-select-sm" value={colFilters.status}
                      onChange={e => setCol('status', e.target.value)}>
                      <option value="">All status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </th>
                  {canWrite && (
                    <th className="text-end">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setColFilters({ courseName: '', category: '', courseCode: '', status: '' })}>
                        Clear
                      </button>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredCourses.length === 0 ? (
                  <tr><td colSpan={canWrite ? 11 : 10} className="text-center py-4 text-muted">
                    {courses.length === 0 ? 'No courses found' : 'No courses match the column filters. Try clearing them.'}
                  </td></tr>
                ) : filteredCourses.map(c => (
                  <tr key={c._id}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span
                          title={c.courseColor || 'Default'}
                          style={{
                            width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                            background: c.courseColor || '#3B82F6',
                            border: '1px solid rgba(15,23,42,0.15)',
                          }}
                        />
                        <div>
                          <div className="fw-medium">{c.courseName}</div>
                          <small className="text-muted">{c.courseId}</small>
                        </div>
                      </div>
                    </td>
                    <td><span className="badge bg-light text-dark border">{c.courseCode}</span></td>
                    <td>
                      {c.category
                        ? <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">{c.category}</span>
                        : <span className="text-muted small">—</span>}
                    </td>
                    <td>
                      {c.discountedPrice > 0 && c.discountedPrice < c.totalFees ? (
                        <div>
                          <span className="text-decoration-line-through text-muted me-1" style={{ fontSize: 11 }}>{fmt(c.totalFees)}</span>
                          <span className="fw-semibold text-success">{fmt(c.discountedPrice)}</span>
                          {c.discountPercent > 0 && <span className="badge bg-success ms-1">{c.discountPercent}%</span>}
                        </div>
                      ) : (
                        <span className="fw-semibold">{fmt(c.totalFees)}</span>
                      )}
                    </td>
                    <td>{c.numberOfMonths ? `${c.numberOfMonths} mo` : '—'}</td>
                    <td>{c.totalHours ? `${c.totalHours} hrs` : '—'}</td>
                    <td><span className="badge bg-light text-dark border">{c.quizzes?.length || 0}</span></td>
                    <td>
                      <span className={`badge bg-${c.status === 'active' ? 'success' : 'secondary'}`}>
                        {c.status}
                      </span>
                      <div className="mt-1">
                        <span className={`badge ${
                          c.visibilityStatus === 'draft'
                            ? 'bg-warning text-dark'
                            : c.visibilityStatus === 'hidden_from_college'
                              ? 'bg-secondary'
                              : 'bg-primary'
                        }`}>
                          {c.visibilityStatus === 'draft'
                            ? 'Draft'
                            : c.visibilityStatus === 'hidden_from_college'
                              ? 'Hidden from college'
                              : 'Published'}
                        </span>
                      </div>
                    </td>
                    {/* URL — link to the course's landing/registration page (admin-set) */}
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {c.url
                        ? <a href={c.url} target="_blank" rel="noopener noreferrer" title={c.url}
                            className="btn btn-sm btn-outline-primary">
                            <i className="bi bi-box-arrow-up-right"></i> Open
                          </a>
                        : <span className="text-muted small">—</span>}
                    </td>
                    {/* View Syllabus — always visible, jumps to course TOC */}
                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-outline-info btn-sm" title="View syllabus (course TOC)"
                        onClick={() => navigate(`/course-content?courseId=${c._id}`)}>
                        <i className="bi bi-list-task"></i> Syllabus
                      </button>
                    </td>
                    {canWrite && (
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          <ColorPickerCell course={c} onSaved={(updated) => {
                            setCourses(prev => prev.map(x => x._id === updated._id ? { ...x, ...updated } : x));
                          }} />
                          <button className="btn btn-outline-primary btn-sm" onClick={() => openEdit(c)}><i className="bi bi-pencil"></i></button>
                          <button className="btn btn-outline-danger btn-sm" onClick={() => setDeleteTarget(c)}><i className="bi bi-trash"></i></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="card-footer d-flex justify-content-between align-items-center py-2">
              <small className="text-muted">Page {page} of {pages} ({total} total)</small>
              <div className="d-flex gap-1">
                <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p-1)}>‹ Prev</button>
                <button className="btn btn-outline-secondary btn-sm" disabled={page >= pages} onClick={() => setPage(p => p+1)}>Next ›</button>
              </div>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {pageTab === 'requests' && canAdmin && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="fw-semibold">
              <i className="bi bi-arrow-left-right me-2"></i>Course Correction Requests
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <input
                className="form-control form-control-sm"
                style={{ width: 220 }}
                placeholder="Search course / requester..."
                value={requestFilter.search}
                onChange={(e) => setRequestFilter((p) => ({ ...p, search: e.target.value }))}
              />
              <select
                className="form-select form-select-sm"
                style={{ width: 150 }}
                value={requestFilter.status}
                onChange={(e) => setRequestFilter((p) => ({ ...p, status: e.target.value }))}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <button className="btn btn-outline-secondary btn-sm" onClick={loadPriceRequests}>
                <i className="bi bi-arrow-clockwise me-1"></i>Refresh
              </button>
            </div>
          </div>
          {requestsLoading ? (
            <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Course</th>
                    <th>Requester</th>
                    <th>Current</th>
                    <th>Requested</th>
                    <th>Reason</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {priceRequests.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-4 text-muted">No correction requests found.</td></tr>
                  ) : priceRequests.map((row) => (
                    <tr key={row._id}>
                      <td>
                        <div className="fw-medium">{row.course?.courseName || 'Course'}</div>
                        <small className="text-muted">{row.course?.courseCode || row.course?.courseId || '-'}</small>
                      </td>
                      <td>
                        <div>{row.requestedBy?.firstName || ''} {row.requestedBy?.lastName || ''}</div>
                        <small className="text-muted">{row.requestedBy?.email || '-'}</small>
                      </td>
                      <td>
                        <div>{fmt(row.currentPricing?.totalFees)}</div>
                        <small className="text-muted">
                          Min {fmt(row.currentPricing?.minPrice)} | Max {fmt(row.currentPricing?.maxPrice || 0)}
                        </small>
                      </td>
                      <td>
                        <div className="fw-semibold text-success">{fmt(row.requestedPricing?.totalFees)}</div>
                        <small className="text-muted">
                          Min {fmt(row.requestedPricing?.minPrice)} | Max {fmt(row.requestedPricing?.maxPrice || 0)}
                        </small>
                      </td>
                      <td style={{ minWidth: 180 }}>
                        <div className="small">{row.reason || '-'}</div>
                        {row.rejectionReason ? <div className="small text-danger mt-1">{row.rejectionReason}</div> : null}
                      </td>
                      <td>
                        <div>{row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-IN') : '-'}</div>
                        <small className="text-muted">
                          {row.createdAt ? new Date(row.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </small>
                      </td>
                      <td>
                        <span className={`badge bg-${row.status === 'approved' ? 'success' : row.status === 'rejected' ? 'danger' : 'warning text-dark'}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="text-end">
                        {row.status === 'pending' ? (
                          <div className="d-flex gap-1 justify-content-end">
                            <button
                              className="btn btn-outline-success btn-sm"
                              disabled={requestBusyId === row._id}
                              onClick={() => handleApproveRequest(row._id)}
                            >
                              {requestBusyId === row._id ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-check-lg me-1"></i>Approve</>}
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              disabled={requestBusyId === row._id}
                              onClick={() => { setRejectTarget(row); setRejectReason(row.rejectionReason || ''); }}
                            >
                              <i className="bi bi-x-lg me-1"></i>Reject
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              title="Delete request"
                              disabled={requestBusyId === row._id}
                              onClick={() => handleDeleteRequest(row)}
                            >
                              {requestBusyId === row._id ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-trash"></i>}
                            </button>
                          </div>
                        ) : (
                          <div className="d-flex gap-2 justify-content-end align-items-center">
                            <small className="text-muted">
                              {row.status === 'approved'
                                ? `Approved ${row.approvedAt ? new Date(row.approvedAt).toLocaleDateString('en-IN') : ''}`
                                : `Rejected ${row.rejectedAt ? new Date(row.rejectedAt).toLocaleDateString('en-IN') : ''}`}
                            </small>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              title="Delete request"
                              disabled={requestBusyId === row._id}
                              onClick={() => handleDeleteRequest(row)}
                            >
                              {requestBusyId === row._id ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-trash"></i>}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Delete modal */}
      <BSModal show={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Course"
        footer={
          <>
            <button className="btn btn-secondary btn-sm" onClick={() => setDeleteTarget(null)}>Cancel</button>
            <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>
              {deleting && <span className="spinner-border spinner-border-sm me-1"></span>}Delete
            </button>
          </>
        }>
        <p>Delete <strong>{deleteTarget?.courseName}</strong>? This cannot be undone.</p>
      </BSModal>

      <BSModal
        show={!!rejectTarget}
        onClose={() => { setRejectTarget(null); setRejectReason(''); }}
        title="Reject Course Price Change"
        footer={
          <>
            <button className="btn btn-secondary btn-sm" onClick={() => { setRejectTarget(null); setRejectReason(''); }}>
              Cancel
            </button>
            <button className="btn btn-danger btn-sm" onClick={handleRejectRequest} disabled={requestBusyId === rejectTarget?._id}>
              {requestBusyId === rejectTarget?._id && <span className="spinner-border spinner-border-sm me-1"></span>}
              Reject Request
            </button>
          </>
        }
      >
        <div className="mb-2">
          <strong>{rejectTarget?.course?.courseName || 'Course'}</strong>
        </div>
        <label className="form-label">Reason</label>
        <textarea
          className="form-control"
          rows={4}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Enter rejection reason..."
        />
      </BSModal>

      {/* Quiz editor */}
      <BSModal show={showQuizModal} onClose={() => setShowQuizModal(false)}
        title={editingQuizIdx !== null ? 'Edit Quiz' : 'Add Quiz'} size="modal-xl"
        footer={
          <>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowQuizModal(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={saveQuiz}>Save Quiz</button>
          </>
        }>
        <div className="row g-3 mb-3">
          <div className="col-md-5">
            <label className="form-label">Quiz Title *</label>
            <input className="form-control" value={quizForm.title}
              onChange={e => setQuizForm(q => ({ ...q, title: e.target.value }))} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Description</label>
            <input className="form-control" value={quizForm.description}
              onChange={e => setQuizForm(q => ({ ...q, description: e.target.value }))} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Time Limit (min, 0=none)</label>
            <input type="number" className="form-control" value={quizForm.timeLimit} min={0}
              onChange={e => setQuizForm(q => ({ ...q, timeLimit: parseInt(e.target.value) || 0 }))} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Total Marks</label>
            <input type="number" className="form-control" value={quizForm.totalMarks} min={1}
              onChange={e => setQuizForm(q => ({ ...q, totalMarks: parseInt(e.target.value) || 0 }))} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Passing Marks</label>
            <input type="number" className="form-control" value={quizForm.passingMarks} min={0}
              onChange={e => setQuizForm(q => ({ ...q, passingMarks: parseInt(e.target.value) || 0 }))} />
          </div>
          <div className="col-md-3 d-flex gap-3 align-items-end pb-1">
            <div className="form-check">
              <input className="form-check-input" type="checkbox" id="chkShuffle" checked={quizForm.shuffleQuestions}
                onChange={e => setQuizForm(q => ({ ...q, shuffleQuestions: e.target.checked }))} />
              <label className="form-check-label" htmlFor="chkShuffle">Shuffle</label>
            </div>
            <div className="form-check">
              <input className="form-check-input" type="checkbox" id="chkNeg" checked={quizForm.allowNegativeMarking}
                onChange={e => setQuizForm(q => ({ ...q, allowNegativeMarking: e.target.checked }))} />
              <label className="form-check-label" htmlFor="chkNeg">Neg. Marks</label>
            </div>
          </div>
          {quizForm.allowNegativeMarking && (
            <div className="col-md-3">
              <label className="form-label">Neg. Value</label>
              <input type="number" className="form-control" value={quizForm.negativeMarkingValue} step="0.25" min={0}
                onChange={e => setQuizForm(q => ({ ...q, negativeMarkingValue: parseFloat(e.target.value) || 0 }))} />
            </div>
          )}
        </div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>Questions ({quizForm.questions?.length || 0})</strong>
          <button className="btn btn-outline-primary btn-sm" onClick={openAddQ}><i className="bi bi-plus me-1"></i>Add Question</button>
        </div>
        {(quizForm.questions || []).map((q, idx) => (
          <div key={idx} className="card mb-2 bg-light border-0">
            <div className="card-body py-2 d-flex justify-content-between align-items-center">
              <div>
                <span className="badge bg-secondary me-2">{q.type}</span>
                <span style={{ fontSize: 13 }}>{q.text || '(no text)'}</span>
                <span className="ms-2 text-muted" style={{ fontSize: 11 }}>{q.marks}M</span>
              </div>
              <div className="d-flex gap-1">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => openEditQ(idx)}><i className="bi bi-pencil"></i></button>
                <button className="btn btn-outline-danger btn-sm" onClick={() => removeQ(idx)}><i className="bi bi-trash"></i></button>
              </div>
            </div>
          </div>
        ))}
      </BSModal>

      {/* Question editor */}
      <BSModal show={showQModal} onClose={() => setShowQModal(false)}
        title={editingQIdx !== null ? 'Edit Question' : 'Add Question'} size="modal-lg"
        footer={
          <>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowQModal(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={saveQ}>Save Question</button>
          </>
        }>
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">Type</label>
            <select className="form-select" value={qForm.type} onChange={e => setQForm(q => ({ ...q, type: e.target.value }))}>
              <option value="mcq">MCQ</option>
              <option value="fill_blank">Fill in the Blank</option>
              <option value="coding">Coding</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Marks</label>
            <input type="number" className="form-control" value={qForm.marks} min={1}
              onChange={e => setQForm(q => ({ ...q, marks: parseInt(e.target.value) || 1 }))} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Neg. Marks</label>
            <input type="number" className="form-control" value={qForm.negativeMarks} min={0} step="0.25"
              onChange={e => setQForm(q => ({ ...q, negativeMarks: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div className="col-12">
            <label className="form-label">Question Text *</label>
            <textarea className="form-control" rows={2} value={qForm.text}
              onChange={e => setQForm(q => ({ ...q, text: e.target.value }))} />
          </div>
          {qForm.type === 'mcq' && [0,1,2,3].map(i => (
            <div key={i} className="col-md-6">
              <label className="form-label d-flex align-items-center gap-2">
                <input type="radio" name="correctOpt" checked={qForm.correctAnswer === i}
                  onChange={() => setQForm(q => ({ ...q, correctAnswer: i }))} />
                Option {i+1} {qForm.correctAnswer === i && <span className="badge bg-success">Correct</span>}
              </label>
              <input className="form-control" value={qForm.options[i] || ''}
                onChange={e => { const opts=[...qForm.options]; opts[i]=e.target.value; setQForm(q=>({...q,options:opts})); }} />
            </div>
          ))}
          {qForm.type === 'fill_blank' && (
            <div className="col-12">
              <label className="form-label">Correct Answer</label>
              <input className="form-control" value={qForm.fillAnswer}
                onChange={e => setQForm(q => ({ ...q, fillAnswer: e.target.value }))} />
            </div>
          )}
          {qForm.type === 'coding' && (
            <>
              <div className="col-12">
                <label className="form-label">Problem Statement</label>
                <textarea className="form-control" rows={2} value={qForm.codingPrompt}
                  onChange={e => setQForm(q => ({ ...q, codingPrompt: e.target.value }))} />
              </div>
              <div className="col-12">
                <label className="form-label">Test Cases <small className="text-muted">(one per line: input::expected)</small></label>
                <textarea className="form-control" rows={3}
                  value={(qForm.testCases||[]).map(tc=>`${tc.input}::${tc.expected}`).join('\n')}
                  onChange={e => {
                    const cases = e.target.value.split('\n').filter(Boolean).map(l => {
                      const [input, expected] = l.split('::');
                      return { input: (input||'').trim(), expected: (expected||'').trim() };
                    });
                    setQForm(q => ({ ...q, testCases: cases }));
                  }} />
              </div>
            </>
          )}
          <div className="col-12">
            <label className="form-label">Explanation</label>
            <textarea className="form-control" rows={2} value={qForm.explanation}
              onChange={e => setQForm(q => ({ ...q, explanation: e.target.value }))} />
          </div>
        </div>
      </BSModal>

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
